// CS 응대 메모 UI의 도메인 어댑터.
// 답례품(giftCsMemo)과 화환(shopOrderCsMemo)은 계약이 같고 타깃 축·역할 라벨만 달라서,
// 화면 컴포넌트는 공용으로 두고 도메인별 훅만 이 어댑터로 갈아 끼운다.

/** 화면이 쓰는 최소 메모 형태 (도메인 타입은 전부 이걸 만족한다) */
export interface CsMemoRecord {
  _id: string;
  by: string;
  byName: string;
  byRole: string;
  adminOnly?: boolean;
  body: string;
  createdAt: Date;
  /** 답례품 전용 하위 단위 — 주문 축 통합 타임라인에서 라벨 붙일 때만 사용 */
  giftCoupon?: string;
  giftDelivery?: string;
}

export interface CsMemoMutation<TInput> {
  mutateAsync: (input: TInput) => Promise<unknown>;
  isLoading: boolean;
}

export interface CsMemoQueryResult {
  /** null = 엔드포인트 미배포(404), [] = 메모 없음, undefined = 아직 모름 */
  data: CsMemoRecord[] | null | undefined;
  isLoading: boolean;
}

export interface CsMemoRoleBadge {
  label: string;
  className: string;
}

/**
 * 역할 배지를 띄울 필요가 있는지 — 작성자 이름이 이미 그 역할을 말하고 있으면 배지는 중복이다.
 * (Admin 계정은 이름 자체가 "관리자"라 `관리자 [관리자]`처럼 두 번 찍혔다)
 *
 * byName을 보고 판단하므로, 이름이 실명인 작성자("김담당")는 배지가 그대로 남는다 —
 * 무조건 숨기면 누가 남긴 메모인지 구분이 사라진다.
 */
export const isRoleBadgeRedundant = (byName: string, badgeLabel: string): boolean =>
  byName.trim() === badgeLabel || byName.includes(badgeLabel);

export interface CsMemoAdapter<TTarget extends object> {
  /** 대상 타임라인 조회 */
  useList: (target: TTarget, enabled: boolean) => CsMemoQueryResult;
  /** 조회 없이 캐시만 구독 (배지 개수용) */
  useCache: (target: TTarget) => CsMemoRecord[] | null | undefined;
  useCreate: () => CsMemoMutation<TTarget & { body: string; adminOnly?: boolean }>;
  useDelete: () => CsMemoMutation<{ memo: CsMemoRecord }>;
  /** 삭제 라우트 미배포(404/405) 판별 — 실패 사유를 구분해 안내한다 */
  isDeleteUnsupported: (e: unknown) => boolean;
  roleBadge: (byRole: string) => CsMemoRoleBadge;
  /** 빈 타깃 방어 (id가 없으면 조회·작성 모두 막는다) */
  hasTarget: (target: TTarget) => boolean;
}
